'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Popover } from '@base-ui/react/popover';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { CaretDownIcon } from '@phosphor-icons/react';
import styles from './DropdownMenu.module.scss';

export interface DropdownMenuProps {
  id: string;
  classNames?: string | string[];
  defaultText?: string;
  selectedOption?: string | number;
  data: {
    name?: string | null;
    value?: string | number | null;
    disabled?: boolean;
  }[];
  handleChange: (value: string) => void;
  hasError?: boolean;
  disabledText?: string;
  /** Optional id of an element that describes the control (e.g. validation error). */
  describedBy?: string;
  elevated?: boolean;
}

const unselectedCode = '0';

type DropdownOption = {
  name?: string | null;
  value?: string | number | null;
  disabled?: boolean;
};

function getOptionValue(entry: DropdownOption): string {
  if (entry.value !== null && entry.value !== undefined && entry.value !== '') {
    return String(entry.value);
  }
  if (entry.name != null && entry.name !== '') {
    return String(entry.name);
  }
  return '';
}

const DropdownMenu = ({
  id,
  classNames,
  defaultText = '',
  selectedOption,
  data,
  handleChange,
  hasError,
  disabledText,
  describedBy,
  elevated,
}: DropdownMenuProps) => {
  const { t } = useTranslation('pdp');
  const listboxId = useId();
  const [listElement, setListElement] = useState<HTMLUListElement | null>(null);
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const pendingScrollFrame = useRef<number | null>(null);
  const pendingScrollTimeout = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const normalizedSelected = String(selectedOption ?? unselectedCode);
  const unavailableLabel = disabledText ?? t('product.unavailable');

  const options = useMemo(
    () =>
      data.map((entry) => {
        const value = getOptionValue(entry);
        const label = entry.disabled
          ? `${entry.name ?? ''} ${unavailableLabel}`.trim()
          : entry.name ?? '';

        return { ...entry, value, label };
      }),
    [data, unavailableLabel]
  );

  const selectedItem = useMemo(
    () => options.find((option) => option.value === normalizedSelected),
    [options, normalizedSelected]
  );

  const displayLabel =
    normalizedSelected === unselectedCode || !selectedItem
      ? defaultText
      : selectedItem.label;

  const pickOption = useCallback(
    (entry: (typeof options)[number]) => {
      if (entry.disabled) return;
      handleChange(entry.value);
      setOpen(false);
    },
    [handleChange]
  );

  const scrollListToSelectedOption = useCallback(() => {
    if (!listElement) return;

    const selectedOptionNode = optionRefs.current[normalizedSelected];

    if (!selectedOptionNode) {
      listElement.scrollTop = 0;
      return;
    }

    const selectedTop = selectedOptionNode.offsetTop;
    const selectedHeight = selectedOptionNode.offsetHeight;
    const listHeight = listElement.clientHeight;
    const maxScrollTop = listElement.scrollHeight - listHeight;
    const centeredScrollTop = selectedTop - (listHeight - selectedHeight) / 2;

    listElement.scrollTop = Math.max(
      0,
      Math.min(centeredScrollTop, maxScrollTop)
    );
  }, [listElement, normalizedSelected]);

  useEffect(() => {
    if (!open || !listElement) return;

    pendingScrollFrame.current = window.requestAnimationFrame(() => {
      pendingScrollFrame.current = window.requestAnimationFrame(
        scrollListToSelectedOption
      );
    });

    return () => {
      if (pendingScrollFrame.current !== null) {
        window.cancelAnimationFrame(pendingScrollFrame.current);
        pendingScrollFrame.current = null;
      }
    };
  }, [open, listElement, scrollListToSelectedOption]);

  useEffect(() => {
    if (!open || !listElement) return;

    pendingScrollTimeout.current = window.setTimeout(() => {
      scrollListToSelectedOption();
    }, 80);

    return () => {
      if (pendingScrollTimeout.current !== null) {
        window.clearTimeout(pendingScrollTimeout.current);
        pendingScrollTimeout.current = null;
      }
    };
  }, [open, listElement, scrollListToSelectedOption]);

  return (
    <div
      className={cn(styles.container, classNames)}
      data-open={open ? 'true' : undefined}
    >
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          id={id}
          type="button"
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-describedby={describedBy}
          aria-invalid={hasError ? true : undefined}
          className={cn(styles.select, styles.trigger, {
            [styles.selectHasError]: hasError,
          })}
        >
          {displayLabel}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner
            side="bottom"
            align="start"
            sideOffset={2}
            collisionAvoidance={{
              side: 'none',
              align: 'shift',
              fallbackAxisSide: 'none',
            }}
            className={cn(styles.positioner, {
              [styles.positionerElevated]: elevated,
            })}
          >
            <Popover.Popup className={styles.popup}>
              <ul
                id={listboxId}
                ref={setListElement}
                role="listbox"
                className={styles.list}
              >
                {options.map((entry, index) => {
                  return (
                    <li
                      key={`option-${entry.value}-${entry.label}-${index}`}
                      role="presentation"
                    >
                      <button
                        ref={(node) => {
                          optionRefs.current[entry.value] = node;
                        }}
                        type="button"
                        role="option"
                        aria-selected={
                          entry.value === normalizedSelected ? true : undefined
                        }
                        disabled={entry.disabled}
                        className={cn(styles.option, {
                          [styles.optionSelected]:
                            entry.value === normalizedSelected,
                        })}
                        onClick={() => pickOption(entry)}
                      >
                        {entry.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
      <CaretDownIcon
        className={styles.icon}
        data-testid="icon-span"
        size={20}
        weight="regular"
        aria-hidden
        color="var(--neutrals-1000)"
      />
    </div>
  );
};

export default DropdownMenu;
